import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import ConsentFormDetails from "../Consent/ConsentForm";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import { fetchConsentForm, updateConsentForm } from "../../store/slices/consentSlice";

const EditConsent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentConsentForm, isLoading, error } = useSelector(
        (state) => state.consent
    );

    const [formData, setFormData] = useState({
        patientId,
        caseId,
        clientName: "",
        relationToClient: "",
        guardianName: "",
        mrNo: "",
        contact: "",
        address: "",
        date: "",
        amount: "",
        paymentStatus: "",
        referenceNo: "",
        paymentDate: "",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (id) dispatch(fetchConsentForm(id));
    }, [dispatch, id]);

    useEffect(() => {
        // ✅ Check if currentConsentForm exists before setting formData
        if (currentConsentForm && Object.keys(currentConsentForm).length > 0) {
            setFormData({
                clientName: currentConsentForm.client_name || "",
                guardianName: currentConsentForm.guardian_name || "",
                relationToClient: currentConsentForm.relation_to_client || "",
                mrNo: currentConsentForm.mr_no || "",
                contact: currentConsentForm.contact_no || "",
                address: currentConsentForm.address || "",
                date: currentConsentForm.consent_date || "",
                amount: currentConsentForm.amount || "",
                paymentStatus: currentConsentForm.payment_status || "",
                referenceNo: currentConsentForm.reference_no || "",
                paymentDate: currentConsentForm.payment_date || "",
            });
        }
    }, [currentConsentForm]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;

        // --- Phone Masking (xxxx-xxxxxxx) ---
        if (name === "contact") {
            const digits = value.replace(/\D/g, "").slice(0, 11);

            if (digits.length <= 4) {
                finalValue = digits;
            } else {
                finalValue = `${digits.slice(0, 4)}-${digits.slice(4)}`;
            }
        }

        setFormData((prev) => ({
            ...prev,
            [name]: finalValue,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.clientName) newErrors.clientName = "Client name is required";
        if (!formData.relationToClient)
            newErrors.relationToClient = "Relation to client is required";
        if (!formData.guardianName) newErrors.guardianName = "Guardian name is required";
        if (!formData.mrNo) newErrors.mrNo = "MR No. is required";
        if (!formData.contact) newErrors.contact = "Contact is required";
        if (!formData.address) newErrors.address = "Address is required";
        if (!formData.date) newErrors.date = "Date is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const toSnakeCase = (obj) => {
        const newObj = {};
        for (const key in obj) {
            const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
            newObj[snakeKey] = obj[key];
        }
        return newObj;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            const payload = {
                patient_id: patientId,
                case_id: caseId,
                client_name: formData.clientName,
                relation_to_client: formData.relationToClient,
                guardian_name: formData.guardianName,
                mr_no: formData.mrNo,
                contact_no: formData.contact,
                address: formData.address,
                consent_date: formData.date,
                amount: formData.amount,
                payment_status: formData.paymentStatus,
                reference_no: formData.referenceNo,
                payment_date: formData.paymentDate,
            };
            await dispatch(updateConsentForm({ case_id: caseId, formData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Consent form updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
            // Stay on edit page
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: err.message || "Failed to update consent form",
                icon: "error",
            });
        }
    };

    if (isLoading) return <div className="p-3">Loading...</div>;
    if (error) return <div className="p-3 text-danger">{error}</div>;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                {/* ✅ Print Button on Top Right */}
                <div className="d-flex justify-content-between align-items-center">
                    <Breadcrumbs />
                    <Link
                        to={`/consents/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">


                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <ConsentFormDetails
                                formData={formData}
                                setFormData={setFormData} // important for repeater updates
                                handleChange={handleChange}
                            />

                            <div className="row">
                                <div className="col-md-3 ms-auto">
                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary w-100"
                                            onClick={() => navigate(-2)}
                                        >
                                            <i className="ri-arrow-left-line me-1"></i> Back
                                        </button>

                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100"
                                        >
                                            <i className="ri-edit-2-line me-1"></i> Update
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditConsent;
